/**
 * Accountability map panel (DC-1824, design §3.6/§5.2 — playbook play 6).
 *
 * The one-screen examiner answer to "who is accountable for this AI
 * decision": four line rows, the current actors on each, and — the part that
 * makes the panel worth having — the lines with nobody on them, SAID OUT
 * LOUD. "No second-line assignment recorded" is a finding; a blank row is
 * not. The map is honest from day one and is never gated on completeness.
 *
 * Three things it deliberately does not do:
 *
 *  - **No materiality tier.** Partner-gated (blueprint row 13); tiering
 *    couples to the R6 aggregation walkthrough, and modelling a tier before
 *    that would invent the partner's framework rather than implement it.
 *  - **No score, count-of-coverage, or "3 of 4 lines assigned" aggregate.**
 *    An aggregate over accountability is the same lie the engine's
 *    no-aggregate rule exists to prevent — the reader counts the rows.
 *  - **No external line.** External assurance attaches to evidence, not to
 *    standing accountability (§3.6).
 *
 * Supersession is visible and NAMED: replacing an actor keeps the prior
 * assignment in the record and retires it against the new one, while adding
 * an actor with nothing named is an addition — co-owners on one line are
 * legal, so an assignment never silently sweeps the line it lands on.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { usePermissions } from '../../hooks/useAuth';
import {
  ACCOUNTABILITY_LINE_MEANING,
  ACCOUNTABILITY_LINES,
  LINE_LABEL,
  type AccountabilityAssignment,
  type AccountabilityLine,
  type AccountabilityMap,
} from './assessmentTypes';
import ActorPicker from './ActorPicker';

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'ready'; map: AccountabilityMap };

/** Title-case the line for sentence-initial use ("First line"). */
function lineHeading(line: AccountabilityLine): string {
  const label = LINE_LABEL[line];
  return label.charAt(0).toUpperCase() + label.slice(1);
}

const AssignmentRow: React.FC<{
  assignment: AccountabilityAssignment;
  line: AccountabilityLine;
}> = ({ assignment, line }) => (
  <li className="uc-accountability-actor" data-testid={`accountability-assignment-${assignment.id}`}>
    <span className="uc-accountability-name">{assignment.actor.displayName}</span>
    <span className="uc-accountability-title">{assignment.actor.roleTitle}</span>
    {/* The registry's own line for this actor. When it differs from the line
        they are assigned to, say so — a third-line actor standing in the
        second line is exactly the kind of thing an examiner asks about, and
        hiding it would make the map less honest than the data. */}
    {assignment.actor.line !== line && (
      <span className="uc-accountability-mismatch">
        registered as {LINE_LABEL[assignment.actor.line]}
      </span>
    )}
  </li>
);

export const UseCaseAccountabilityMap: React.FC<{ useCaseId: string }> = ({ useCaseId }) => {
  const { get, post } = useApiClient();
  // The same gate the server enforces (requireAnalystOrAdmin) — role, not
  // journey/dataset permission, so the surface cannot drift from the route.
  const { isAdmin, isAnalyst } = usePermissions();
  const canEdit = isAdmin || isAnalyst;
  const [state, setState] = useState<LoadState>({ phase: 'loading' });

  // The assign form. No default line and no default actor — an accountability
  // assignment is never assumed, the same no-pre-selection rule the judgement
  // and the position stance follow.
  const [formLine, setFormLine] = useState<AccountabilityLine | null>(null);
  const [formActorId, setFormActorId] = useState<string | null>(null);
  const [supersedes, setSupersedes] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const res = await get<{ data?: { accountability?: AccountabilityMap } }>(
        `/api/use-cases/${useCaseId}/accountability`
      );
      const map = res.data?.accountability;
      setState(map ? { phase: 'ready', map } : { phase: 'error' });
    } catch {
      setState({ phase: 'error' });
    }
  }, [get, useCaseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = useCallback(async () => {
    if (!formLine || !formActorId) return;
    setSaving(true);
    setRefusal(null);
    try {
      await post(`/api/use-cases/${useCaseId}/accountability/assignments`, {
        line: formLine,
        actorId: formActorId,
        ...(supersedes ? { supersedes } : {}),
      });
      setFormLine(null);
      setFormActorId(null);
      setSupersedes(null);
      await load();
    } catch (error) {
      setRefusal(
        (error as { body?: { error?: { message?: string } } }).body?.error?.message ??
          'The assignment was not recorded.'
      );
    } finally {
      setSaving(false);
    }
  }, [post, useCaseId, formLine, formActorId, supersedes, load]);

  if (state.phase === 'loading') {
    return <div className="uc-accountability" aria-busy="true" data-testid="accountability-loading" />;
  }

  if (state.phase === 'error') {
    return (
      <div className="uc-accountability">
        <div className="banking-grid-error" role="alert">
          <p>The accountability map could not be read.</p>
          <button type="button" className="banking-btn banking-btn-secondary" onClick={() => void load()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Only CURRENT assignments on the chosen line can be superseded — the
  // dropdown is built from the map itself, so an already-retired assignment
  // is never offerable.
  const supersedable =
    formLine === null
      ? []
      : (state.map.lines.find((entry) => entry.line === formLine)?.assignments ?? []);

  return (
    <section className="uc-accountability" aria-label="Accountability map" data-testid="accountability-map">
      <p className="uc-accountability-lede">
        Who is accountable for this use case, by line of defense. A line with nobody on it is
        stated, not left blank — an unassigned line is a finding in its own right. External
        assurance is not a line here: it attaches to evidence, not to standing accountability.
      </p>

      <ul className="uc-accountability-lines">
        {state.map.lines.map((entry) => (
          <li
            key={entry.line}
            className="uc-accountability-line"
            data-testid={`accountability-line-${entry.line}`}
          >
            <div className="uc-accountability-line-head">
              <h4>{lineHeading(entry.line)}</h4>
              <p className="uc-accountability-meaning">{ACCOUNTABILITY_LINE_MEANING[entry.line]}</p>
            </div>
            {entry.assignments.length === 0 ? (
              <p
                className="uc-accountability-absent"
                data-testid={`accountability-absent-${entry.line}`}
              >
                No {LINE_LABEL[entry.line]} assignment recorded.
              </p>
            ) : (
              <ul className="uc-accountability-actors">
                {entry.assignments.map((assignment) => (
                  <AssignmentRow key={assignment.id} assignment={assignment} line={entry.line} />
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>

      {canEdit && (
        <form
          className="uc-accountability-form"
          data-testid="accountability-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (formLine && formActorId) void submit();
          }}
        >
          <h4>Assign accountability</h4>
          <label htmlFor="uc-accountability-line">Line of defense</label>
          <select
            id="uc-accountability-line"
            value={formLine ?? ''}
            disabled={saving}
            onChange={(event) => {
              const next = event.target.value === '' ? null : (event.target.value as AccountabilityLine);
              setFormLine(next);
              // The supersession target belongs to the line it was chosen on.
              setSupersedes(null);
            }}
          >
            {/* No default: a line is chosen, never assumed. */}
            <option value="">Choose a line…</option>
            {ACCOUNTABILITY_LINES.map((line) => (
              <option key={line} value={line}>
                {lineHeading(line)}
              </option>
            ))}
          </select>

          <ActorPicker
            idPrefix="uc-accountability"
            label="Who is accountable"
            value={formActorId}
            onChange={setFormActorId}
            disabled={saving}
          />

          {supersedable.length > 0 && (
            <>
              <label htmlFor="uc-accountability-supersedes">Replaces (optional)</label>
              <select
                id="uc-accountability-supersedes"
                value={supersedes ?? ''}
                disabled={saving}
                onChange={(event) => setSupersedes(event.target.value === '' ? null : event.target.value)}
              >
                <option value="">Nobody — this is an additional accountable party</option>
                {supersedable.map((assignment) => (
                  <option key={assignment.id} value={assignment.id}>
                    {assignment.actor.displayName} — {assignment.actor.roleTitle}
                  </option>
                ))}
              </select>
              <p className="uc-accountability-hint">
                Naming someone here retires their assignment against this one, visibly — the prior
                assignment stays on the record. Leaving it unset adds a co-owner; nobody is
                replaced by accident.
              </p>
            </>
          )}

          <button
            type="submit"
            className="banking-btn banking-btn-primary"
            data-testid="record-assignment"
            disabled={!formLine || !formActorId || saving}
          >
            {saving ? 'Recording…' : 'Record assignment'}
          </button>
          {(!formLine || !formActorId) && (
            <p className="uc-accountability-hint">
              A line and a named actor are both required — an accountability map with an unnamed
              owner answers nothing.
            </p>
          )}
          {refusal && (
            <p className="uc-accountability-refusal" role="alert">
              {refusal}
            </p>
          )}
        </form>
      )}
    </section>
  );
};

export default UseCaseAccountabilityMap;
